import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  isPlatformBrowser,
  NgClass,
  NgIf,
  NgOptimizedImage
} from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AppSettings } from '../../app.settings';
import { ScreenSizeService } from '@Shared/services';
import { AuthActionType } from '@App/pages/auth/auth.type';
import { Drawer } from 'primeng/drawer';
import { BannerConfig, IHeaderMenuItem } from '@App/common/interfaces';
import { AuthService } from '@App/pages/auth/services';
import {
  CommonBindingDataService,
  CountryInputComponent,
  NotificationService,
  StorageService
} from '@App/shared';
import { BannerTypes, PitchCardTypeEnum } from '@App/common/enums';
import { LandingPageService } from '@App/pages/landing-page/landing-page.service';
import { UserCommonApiService } from '@App/common/services';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { SupportRequestTypeEnum } from '@App/common/enums/support-request-type.enum';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { UserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';
import { ResourceDropdownComponent, DropdownItem } from '@App/shared';
import { EXTERNAL_URLS } from '@App/shared';
import { SubscriptionManagementService } from '@App/pages/pricing/services/subscription-management.service';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-welcome-page-header',
  styleUrls: ['./welcome-page-header.component.scss'],
  templateUrl: './welcome-page-header.component.html',
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ButtonModule,
    TranslateModule,
    TooltipModule,
    DialogModule,
    ReactiveFormsModule,
    InputTextModule,
    CountryInputComponent,
    TextareaModule,
    RouterLink,
    NgIf,
    RouterLinkActive,
    NgClass,
    NgOptimizedImage,
    ToastModule,
    MessageModule,
    UserProfileMenuComponent,
    ResourceDropdownComponent
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class WelcomePageHeaderComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  createOrEdit = 'Create A';
  emailId: string;
  isBrowser = false;
  isBusiness: boolean;
  items: IHeaderMenuItem[];
  unauthorizedItems: IHeaderMenuItem[];
  defaultProfileUrl: string = AppSettings.DEFAULT_PROFILE_IMAGE_URL;
  profilePictureThumbnailUrl = signal(this.defaultProfileUrl);
  serviceUrl: string;
  signedIn = signal(false);
  subscription: Subscription;
  userChecked = false;
  userFullName: string;
  userFirstName = signal('');
  isMobile: boolean;
  isDesktop: boolean = false;
  blockEmployerPortalLink: boolean;

  $createOrEditSubject: Subscription;
  $hideLinks: Subscription;
  $updatedUserInfo: Subscription;
  $isSignedInSubscription = new Subscription();
  bannerConfig: BannerConfig = {} as BannerConfig;
  BannerTypesEnum = BannerTypes;
  businessType: PitchCardTypeEnum = PitchCardTypeEnum.Basic;
  contactForm: FormGroup = new FormGroup<any>({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    subject: new FormControl('', Validators.required),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(AppSettings.EMAIL_PATTERN)
    ]),
    phoneNumber: new FormControl(''),
    message: new FormControl('')
  });

  @Input() searchPage = true;
  @Input() elementToAppend: Drawer;

  isProfileMenuVisible = false;
  isGuestMenuVisible = false;

  @ViewChild('profileMenuContainer') profileMenuContainer!: ElementRef;
  @ViewChild('guestMenuContainer') guestMenuContainer!: ElementRef;
  @ViewChild('headerBar') headerBar!: ElementRef;
  resourceMenuItems: DropdownItem[];
  isContactDialogActive: boolean;
  isBurgerMenuVisible: boolean = false;
  cardTypeOptions: any[] = [
    { label: 'Business', value: PitchCardTypeEnum.Basic },
    { label: 'Resume', value: PitchCardTypeEnum.Resume }
  ];
  cardTypePanelStyle = {
    'margin-top': '15px',
    background: 'white',
    border: '1px solid #E3E3E3',
    'border-radius': '12px',
    overflow: 'hidden',
    'max-width': '209px',
    width: '100%'
  };
  contactFormType: SupportRequestTypeEnum;
  isAffiliatePage: boolean;
  isAffiliateFlow: boolean;
  isWarningBannerVisible = false;
  mobileResourceMenu = [
    {
      label: 'Webinars',
      url: EXTERNAL_URLS.WEBINARS,
      command: () => window.open(EXTERNAL_URLS.WEBINARS, '_blank')
    },
    {
      label: 'Help Center',
      command: () => this.router.navigate(['/help-center'])
    },
    {
      label: 'Contact Sales',
      command: () => this.openContactForm(0)
    },
    {
      label: 'White Label Partners',
      command: () =>
        window.open('https://help.pitch59.com/white-label-partners', '_blank')
    }
  ];
  private injector = inject(Injector);
  private $isDestroyed: Subject<void>;

  constructor(
    private commonBindingDataService: CommonBindingDataService,
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService,
    private userCommonService: UserCommonApiService,
    private screenSizeService: ScreenSizeService,
    private notificationService: NotificationService,
    private landingPageService: LandingPageService,
    private authService: AuthService,
    private subscriptionManagementService: SubscriptionManagementService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isMobile = this.screenSizeService.isMobile;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.onResized();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isAffiliatePage = this.router.url.split('?')[0] === '/affiliate';
        this.businessType = this.router.url.includes('jobs')
          ? PitchCardTypeEnum.Resume
          : PitchCardTypeEnum.Basic;
      }
    });
  }

  get isHasPitchCard(): boolean {
    return this.userCommonService.isHasPitchCard;
  }

  get isLandingPage() {
    const baseUrl = this.router.url.split('?')[0];
    return baseUrl === '/' || baseUrl.includes('jobs');
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event) {
    const target = event.target as HTMLElement;

    if (!this.isClickInsideMenu(target)) {
      this.closeAllMenus();
    }
  }

  goToPage(link: string, query?: any) {
    this.landingPageService.smoothVerticalScrolling(0, 300);
    setTimeout(() => {
      query
        ? this.router.navigate([link], { queryParams: query })
        : this.router.navigate([link]);
    }, 400);
  }

  refreshSearch() {
    if (
      this.router.url.includes('search-result') ||
      this.router.url.includes('search')
    ) {
      this.userCommonService.onSearch.next(true);
    }
    this.router.navigate(['/search'], {
      skipLocationChange: this.router.url.includes('search'),
      queryParams: {
        types: this.route.snapshot.queryParams?.types
          ? this.route.snapshot.queryParams.types
          : 'basic.employee'
      }
    });
  }

  ngOnInit() {
    // const {addPaymentMethod, minPlan} = this.userCommonService.recommendedOptions || {}
    // console.log()
    toObservable(this.userCommonService.recommendedOptions, {injector: this.injector})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(options => {
        if(options?.addPaymentMethod || options?.minPlan) {
          this.isWarningBannerVisible = true;
        }
      });
    this.$isDestroyed = new Subject<void>();
    this.businessType = this.router.url.includes('jobs')
      ? PitchCardTypeEnum.Resume
      : PitchCardTypeEnum.Basic;

    // Initialize screen size properties
    this.isMobile = this.screenSizeService?.isMobile || false;
    this.isDesktop = this.screenSizeService?.isDesktop || false;

    if (this.isBrowser) {
      this.$createOrEditSubject =
        this.userCommonService.createOrEditSubject.subscribe((value) => {
          this.createOrEdit = value;
        });
      this.serviceUrl = this.route.snapshot.queryParams.serviceUrl;
      this.userCommonService.createOrEdit();
      this.initSetup();
      this.reloadInit();
      this.signedInSubscription();
      this.bannerConfig = this.landingPageService.getBannerConfig(
        this.BannerTypesEnum.MainBanner
      );
    }

    this.userCommonService.openSupportWindowSub
      .pipe(takeUntil(this.$isDestroyed))
      .subscribe({
        next: (type) => {
          if (type !== null) {
            this.openContactForm(type);
          }
        }
      });

    this.isBusiness = this.router.url == '/jobs';
    this.resourceMenuItems = [
      {
        label: 'Video Tutorial Center',
        url: EXTERNAL_URLS.VIDEO_TUTORIALS,
        action: () => window.open(EXTERNAL_URLS.VIDEO_TUTORIALS, '_blank')
      },
      {
        label: 'Webinars',
        url: EXTERNAL_URLS.WEBINARS,
        action: () => window.open(EXTERNAL_URLS.WEBINARS, '_blank')
      },
      {
        label: "FAQ's",
        url: EXTERNAL_URLS.FAQS,
        action: () => window.open(EXTERNAL_URLS.FAQS, '_blank')
      },
      {
        label: 'Help Center',
        action: () => this.router.navigate(['/help-center'])
      },
      {
        label: 'Blog',
        url: EXTERNAL_URLS.BLOG,
        action: () => window.open(EXTERNAL_URLS.BLOG, '_blank')
      },
      {
        label: 'Contact Sales',
        action: () => this.openContactForm(0)
      },
      {
        label: 'Contact Support',
        action: () => {
          this.openContactForm(1);
        }
      },
      {
        label: 'White Label Partners',
        action: () =>
          window.open('https://help.pitch59.com/white-label-partners', '_blank')
      }
    ];
  }

  ngOnDestroy() {
    if (this.isBrowser && typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    if (this.$hideLinks) {
      this.$hideLinks.unsubscribe();
    }
    if (this.$updatedUserInfo) {
      this.$updatedUserInfo.unsubscribe();
    }
    if (this.$createOrEditSubject) {
      this.$createOrEditSubject.unsubscribe();
    }
    if (this.$isSignedInSubscription) {
      this.$isSignedInSubscription.unsubscribe();
    }
    this.$isDestroyed.next();
    this.$isDestroyed.complete();
  }

  reloadInit() {
    this.$hideLinks = this.userCommonService.hideLinks.subscribe(
      (isLoadHeader: boolean) => {
        if (isLoadHeader) {
          this.initSetup();
        } else {
          this.setUnauthorizedItems();
        }
      }
    );
  }

  onResized() {
    this.blockEmployerPortalLink =
      this.isMobile ||
      (typeof window !== 'undefined' && window.innerWidth < 1024);
  }

  initSetup() {
    if (this.$updatedUserInfo) {
      this.$updatedUserInfo.unsubscribe();
    }

    const userDetailsString = this.storageService.getItem(
      AppSettings.USER_DETAILS
    );
    let userDetails = null;

    if (userDetailsString && typeof userDetailsString === 'string') {
      try {
        userDetails = JSON.parse(userDetailsString);
      } catch (error) {
        console.error('Error parsing user details:', error);
        userDetails = null;
      }
    }

    const token = this.storageService.getItemFromCookies(AppSettings.TOKEN_KEY);

    if (userDetails && token) {
      this.updateUserInfoAndMenuItems(userDetails);
    } else {
      this.userChecked = true;
      this.clearSession();
      this.setUnauthorizedItems();
    }

    interface UserDataSubscriber {
      fullName: string;
      emailId: string;
      profilePictureThumbnailUrl: string;
    }

    this.$updatedUserInfo = this.userCommonService.updatedUserInfo.subscribe(
      (data: UserDataSubscriber) => {
        this.userFullName = data.fullName;
        this.profilePictureThumbnailUrl.set(
          data.profilePictureThumbnailUrl ||
            '/assets/images/default_profile.svg'
        );
        if (this.userCommonService.isAuthenticated() && data.emailId) {
          this.subscriptionManagementService.getCurrentPlan().subscribe(() => {
            this.setItems();
          });
        }
      }
    );

    this.subscriptionManagementService.currentPlan$
      .pipe(takeUntil(this.$isDestroyed))
      .subscribe(() => {
        if (this.signedIn()) {
          this.setItems();
        }
      });
  }

  updateUserInfoAndMenuItems(userDetails: any) {
    this.userCommonService.getUserProfile(userDetails.userId).subscribe({
      next: ({ data }) => {
        if (data) {
          this.userFirstName.set(data.firstName);
          this.userFullName = data.firstName + ' ' + data.lastName;
          this.emailId = data.emailId;
          this.signedIn.set(true);
          this.userChecked = true;

          if (userDetails && data.profilePictureThumbnailUrl) {
            this.profilePictureThumbnailUrl.set(
              data.profilePictureThumbnailUrl ||
                '/assets/images/default_profile.svg'
            );
          }

          if (this.router.url.includes('sign-in')) {
            this.userCommonService.profileHasBeenReceived.next(true);
          }

          this.storageService.setItem(
            AppSettings.IS_RESUME_EXIST,
            this.userCommonService.isResumeCreated
          );
          if (this.userCommonService.userOrganizationProgress?.length) {
            this.storageService.setItem(
              AppSettings.ORGANIZATION_PROGRESS,
              this.userCommonService.userOrganizationProgress[0]
            );
          } else {
            this.storageService.removeItem(AppSettings.ORGANIZATION_PROGRESS);
          }

          this.subscriptionManagementService.getCurrentPlan().subscribe(() => {
            this.setItems();
          });
        }
      },
      error: () => {
        this.userChecked = true;
        this.doSignOut();
      }
    });
    this.userCommonService.checkPitchCardAvailability().subscribe({
      next: ({ data }) => {
        this.userCommonService.isHasPitchCard = data.hasAnyBusiness;
      }
    });
  }

  setItems() {
    const hasOrganizationProgress = !!(
      this.userCommonService.userOrganizationProgress?.length ||
      this.storageService.getItem(AppSettings.ORGANIZATION_PROGRESS)
    );

    const isStarterPlan = this.subscriptionManagementService.isStarterPlan();
    const isProPlusPlan = this.subscriptionManagementService.isProPlusPlan();
    const hasPricingPlan = this.subscriptionManagementService.hasPricingPlan();

    const addNewPitchCardItem: IHeaderMenuItem[] = [];
    if (isStarterPlan || isProPlusPlan) {
      addNewPitchCardItem.push({
        label: 'Add New PitchCard',
        route: isStarterPlan ? '/pricing' : undefined,
        icon: 'assets/images/my-account-images/profile-sidebar-images/my-pitchcards.svg',
        command: isProPlusPlan
          ? () => {
              this.router.navigate(['/additional-cards']);
            }
          : undefined
      } as IHeaderMenuItem);
    }

    this.items = [
      ...addNewPitchCardItem,
      {
        label: 'My PitchCards',
        route: 'account/my-pitchcards',
        icon: 'assets/images/my-account-images/profile-sidebar-images/my-pitchcards.svg'
      },
      ...(hasPricingPlan
        ? [
            {
              label: 'Manage Subscriptions',
              route: 'account/my-subscriptions',
              icon: 'assets/images/my-account-images/profile-sidebar-images/my-pitchcards.svg'
            } as IHeaderMenuItem
          ]
        : []),
      {
        label: 'My Network',
        route: 'account/pockets',
        icon: 'assets/images/my-account-images/profile-sidebar-images/pockets.svg'
      },
      // Add Team Manager only if user has organization progress
      ...(hasOrganizationProgress
        ? [
            {
              label: 'Team Manager',
              route: 'account/employer-portal',
              icon: 'assets/images/my-account-images/profile-sidebar-images/employer-portal.svg',
              isDesktopOnly: true
            } as IHeaderMenuItem
          ]
        : []),
      {
        label: 'Affiliate Hub',
        route: 'account/affiliate-hub',
        icon: 'assets/images/affiliate-logo.svg'
      },
      {
        label: 'PitchCard Pricing',
        route: '/pricing',
        icon: 'assets/images/my-account-images/profile-sidebar-images/commissions.svg'
      }
    ];
    if (this.isMobile) {
      this.items = [...this.items, ...this.mobileResourceMenu];
    }
  }

  setUnauthorizedItems() {
    this.unauthorizedItems = [
      {
        label: 'Download App',
        command: () => {
          window.open(this.setDownloadSourceLink(), '_blank');
        }
      }
    ];
    if (!this.isAffiliatePage && !this.isAffiliateFlow) {
      this.unauthorizedItems.push(
        {
          label: 'PitchCard Pricing',
          route: '/pricing',
          icon: 'assets/images/my-account-images/profile-sidebar-images/commissions.svg'
        },
        {
          label: 'Search',
          route: '/search',
          icon: 'assets/images/search-green.svg',
          command: this.refreshSearch
        }
      );
    }
    this.unauthorizedItems = [
      ...this.unauthorizedItems,
      ...this.mobileResourceMenu
    ];
  }

  doSignOut() {
    this.userCommonService.logout().subscribe({
      next: () => {
        this.isWarningBannerVisible = false;
        this.signedIn.set(false);
        this.clearSession();
        if (this.router.url === '/affiliate') {
          this.router.navigate(['/affiliate']);
        } else {
          this.goToPage('');
        }
      },
      error: () => {
        this.clearSession();
      }
    });
  }

  clearSession() {
    this.userCommonService.clearSession();

    if (!this.route.snapshot.queryParams.signup) {
      this.userCommonService.signupUserData = null;
    }
  }

  goToAddbusiness() {
    this.storageService.setItemInCookies(AppSettings.DRAFT_BUSINESS_ID, null);

    if (this.createOrEdit === 'Finish') {
      this.router.navigate(['create']);
      return;
    }

    this.router.navigate(['cards-packages']);
  }

  setDownloadSourceLink() {
    return navigator.userAgent.match(/Android/i)
      ? 'https://play.google.com/store/apps/details?id=com.pitch.fiftynine'
      : 'https://apps.apple.com/us/app/pitch59/id1502156356';
  }

  signedInSubscription() {
    this.userCommonService.isSignIn
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.signedIn.set(val);
      });
  }

  setNavigateUrl(url) {
    if (this.route.snapshot.queryParams.serviceUrl) {
      this.router.navigate([url], {
        queryParams: {
          serviceUrl: this.route.snapshot.queryParams.serviceUrl
        }
      });
    } else {
      this.router.navigate([url]);
    }
  }

  desktopOnlyItem(menuItem: string) {
    if (!this.isDesktop) {
      this.notificationService.showNotification(
        'WARNING',
        this.commonBindingDataService.getLabel(menuItem) +
          ' ' +
          this.commonBindingDataService.getLabel('msg_only_for_desktop')
      );
    } else {
      this.landingPageService.setLastPageUrl();
    }
  }

  scrollTop() {
    this.landingPageService.smoothVerticalScrolling(0, 300);
  }

  openAuth(type: AuthActionType) {
    this.authService.setAction(type);
  }

  onLandingPageChange({ value }: SelectChangeEvent) {
    switch (value) {
      case PitchCardTypeEnum.Basic:
        if (!this.router.url.includes('welcome')) {
          this.router.navigate(['']);
        }
        break;
      case PitchCardTypeEnum.Resume:
        if (!this.router.url.includes('jobs')) {
          this.router.navigate(['/jobs']);
        }
        break;
    }
  }

  navigateToPricing() {
    this.router.navigate(['/pricing']);
  }

  sendHelpRequest() {
    let body = this.contactForm.value;
    const userData = this.storageService.getItem(
      AppSettings.USER_DETAILS
    ) as any;
    if (userData?.userId) {
      body = { ...this.contactForm.value, userId: userData.userId };
    }
    this.userCommonService
      .sendHelpRequest(body, this.contactFormType)
      .subscribe({
        next: () => {
          this.notificationService.showNotification(
            'SUCCESS',
            'Your request has been sent successfully'
          );
          this.isContactDialogActive = false;
          this.contactFormType = null;
          this.contactForm.reset();
        },
        error: () => {
          this.notificationService.showNotification(
            'ERROR',
            'Failed to send request'
          );
        }
      });
  }

  resetContactForm() {
    this.isContactDialogActive = false;
    this.contactFormType = null;
    this.contactForm.reset();
  }

  navigateTo(route: string) {
    if (this.route.snapshot.queryParams.discountCode) {
      this.router.navigate(['/' + route], {
        queryParams: {
          discountCode: this.route.snapshot.queryParams.discountCode
        }
      });
    } else {
      this.router.navigate(['/' + route]);
    }
  }

  navigateToAffiliateOnboarding() {
    if (sessionStorage.getItem('DiscountCode')) {
      this.router.navigate(['/pricing'], {
        queryParams: {
          discountCode: sessionStorage.getItem('DiscountCode')
        }
      });
    } else {
      this.router.navigate(['/pricing']);
    }
  }

  navigateToMyPitchCards() {
    this.router.navigate(['/account/my-pitchcards']);
  }

  navigateToMySubscriptions() {
    this.router.navigate(['/account/my-subscriptions']);
  }

  dismissWarningBanner() {
    this.isWarningBannerVisible = false;
  }

  toggleBurgerMenu() {
    this.isBurgerMenuVisible = !this.isBurgerMenuVisible;
  }

  hideBurgerMenu() {
    this.isBurgerMenuVisible = false;
  }

  toggleProfileMenu(event: Event) {
    event.stopPropagation();
    this.isProfileMenuVisible = !this.isProfileMenuVisible;
    this.isGuestMenuVisible = false;
    this.updateBodyScrollLock();
  }

  toggleGuestMenu(event: Event) {
    event.stopPropagation();
    this.isGuestMenuVisible = !this.isGuestMenuVisible;
    this.isProfileMenuVisible = false;
    this.updateBodyScrollLock();
  }

  closeAllMenus() {
    this.isProfileMenuVisible = false;
    this.isGuestMenuVisible = false;
    this.updateBodyScrollLock();
  }

  hideMenu() {
    this.isProfileMenuVisible = false;
    this.updateBodyScrollLock();
  }

  hideGuestMenu() {
    this.isGuestMenuVisible = false;
    this.updateBodyScrollLock();
  }

  hideProfileMenu() {
    this.isProfileMenuVisible = false;
    this.updateBodyScrollLock();
  }

  private updateBodyScrollLock(): void {
    if (!this.isBrowser || typeof document === 'undefined' || !this.isMobile) {
      return;
    }
    const lock = this.isProfileMenuVisible || this.isGuestMenuVisible;
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  onMenuItemClick(item: any) {
    this.closeAllMenus();

    if (item.type === 'logout') {
      this.doSignOut();
    } else if (item.type === 'settings') {
    } else if (item.command) {
      item.command();
    } else if (item.label === 'label_sign_in') {
      this.openAuth('SIGN_IN');
    } else if (item.label === 'label_sign_up') {
      this.openAuth('SIGN_UP');
    }
  }

  private isClickInsideMenu(target: HTMLElement): boolean {
    const menuContainers = [
      this.profileMenuContainer?.nativeElement,
      this.guestMenuContainer?.nativeElement
    ];

    return menuContainers.some(
      (container) =>
        container && (container.contains(target) || container === target)
    );
  }

  private openContactForm(type: SupportRequestTypeEnum) {
    this.isContactDialogActive = true;
    this.contactFormType = type;
  }
}
